/**
 * Security configuration and environment flags
 * Centralized location for all security-related settings
 */

export interface SecurityFlags {
  // External resource controls
  enableAnalytics: boolean;
  enableGoogleAnalytics: boolean;
  enableExternalFonts: boolean;
  enableCdnImages: boolean;
  
  // Development vs production
  isDevelopment: boolean;
  isProduction: boolean;
  
  // Feature flags
  enableErrorReporting: boolean;
  enablePerformanceMonitoring: boolean;
  enableDebugMode: boolean;
}

/**
 * Get security configuration from environment variables
 */
export function getSecurityConfig(): SecurityFlags {
  const env = typeof window !== 'undefined' ? import.meta.env : process.env;
  
  return {
    // Analytics and tracking - SECURE DEFAULT: OFF
    enableAnalytics: env.VITE_ENABLE_ANALYTICS === 'true',
    enableGoogleAnalytics: env.VITE_ENABLE_GA === 'true' && !!env.NEXT_PUBLIC_GA_ID,
    
    // External resources - SECURE DEFAULT: OFF for CDN, ON for fonts (better UX)
    enableExternalFonts: env.VITE_ENABLE_EXTERNAL_FONTS === 'true', // default false (secure)
    enableCdnImages: env.VITE_ENABLE_CDN_IMAGES === 'true', // default false (secure)
    
    // Environment detection
    isDevelopment: env.NODE_ENV === 'development',
    isProduction: env.NODE_ENV === 'production',
    
    // Feature flags - SECURE DEFAULTS
    enableErrorReporting: env.VITE_ENABLE_ERROR_REPORTING !== 'false', // default true (needed for monitoring)
    enablePerformanceMonitoring: env.VITE_ENABLE_PERFORMANCE_MONITORING === 'true', // default false
    enableDebugMode: env.VITE_DEBUG === 'true' || env.NODE_ENV === 'development', // default false except dev
  };
}

/**
 * Security allowlists for external domains
 */
export const SECURITY_ALLOWLISTS = {
  // CDN and external resources
  fonts: ['rsms.me'],
  images: ['cdn.jsdelivr.net', 'upload.wikimedia.org'],
  scripts: ['js.stripe.com', 'va.vercel-scripts.com'],
  analytics: ['www.googletagmanager.com', 'www.google-analytics.com'],
  
  // API endpoints
  apis: ['api.stripe.com', 'hooks.stripe.com'],
  
  // Supabase (dynamically added based on config)
  supabase: [], // Populated at runtime
} as const;

/**
 * Content Security Policy directives
 */
export const CSP_DIRECTIVES = {
  'default-src': ["'self'"],
  'script-src': ["'self'"],
  'style-src': ["'self'", "'unsafe-inline'"], // Tailwind requires unsafe-inline
  'img-src': ["'self'", "data:", "blob:"],
  'font-src': ["'self'"],
  'connect-src': ["'self'"],
  'media-src': ["'self'"],
  'object-src': ["'none'"],
  'base-uri': ["'none'"], // More restrictive than 'self'
  'form-action': ["'self'"],
  'frame-ancestors': ["'none'"],
  'frame-src': ["'self'"],
  'worker-src': ["'self'"],
  'manifest-src': ["'self'"],
  'upgrade-insecure-requests': [],
} as const;

/**
 * Security headers for different environments
 */
export const SECURITY_HEADERS = {
  // Always applied
  common: {
    'X-Frame-Options': 'DENY',
    'X-Content-Type-Options': 'nosniff',
    'Referrer-Policy': 'strict-origin-when-cross-origin',
    'Permissions-Policy': 'camera=(), microphone=(), geolocation=()',
  },
  
  // Development only
  development: {
    'X-XSS-Protection': '1; mode=block',
  },
  
  // Production only
  production: {
    'Strict-Transport-Security': 'max-age=31536000; includeSubDomains; preload',
    'X-XSS-Protection': '1; mode=block',
  },
} as const;

/**
 * Resource integrity hashes (SRI) - Updated regularly
 */
export const RESOURCE_INTEGRITY = {
  // External fonts - Inter v4.0
  'inter-font': {
    url: 'https://rsms.me/inter/inter.css',
    integrity: 'sha384-4lqSq3YfZvFksOlTTUyoYI+vZDMwz8ULDXbX7epYvlnH8YZbQXb8iOjUV8YJWM2D',
    crossorigin: 'anonymous'
  },
  
  // Stripe.js - NOTE: Stripe updates frequently, no SRI
  // Use CSP to restrict to js.stripe.com only
  
  // Google Analytics - NOTE: Google updates frequently, no SRI
  // Use CSP to restrict to googletagmanager.com only
  
  // Payment icons fallbacks (if using local versions)
  'visa-icon': {
    url: '/icons/visa.svg',
    integrity: 'sha384-local-visa-hash',
    local: true
  },
  'mastercard-icon': {
    url: '/icons/mastercard.svg', 
    integrity: 'sha384-local-mastercard-hash',
    local: true
  },
  'apple-pay-icon': {
    url: '/icons/apple-pay.svg',
    integrity: 'sha384-local-apple-pay-hash', 
    local: true
  }
} as const;

/**
 * CSP Reporting configuration
 */
export const CSP_REPORTING = {
  // CSP violation reporting endpoint
  reportUri: '/api/csp-violations',
  
  // Report-To header configuration for newer browsers
  reportTo: {
    group: 'csp-violations',
    max_age: 86400, // 24 hours
    endpoints: [
      { url: '/api/csp-violations' }
    ]
  }
} as const;

/**
 * Check if external domain is allowed
 */
export function isDomainAllowed(url: string, category: keyof typeof SECURITY_ALLOWLISTS): boolean {
  try {
    const urlObj = new URL(url);
    const allowedDomains = SECURITY_ALLOWLISTS[category];
    
    return allowedDomains.some(domain => 
      urlObj.hostname === domain || urlObj.hostname.endsWith(`.${domain}`)
    );
  } catch {
    return false;
  }
}

/**
 * Generate environment-specific CSP
 */
export function generateCSP(config: SecurityFlags): string {
  const directives = { ...CSP_DIRECTIVES };
  
  // Add Supabase domains (comprehensive coverage)
  const supabaseUrl = typeof window !== 'undefined' 
    ? import.meta.env.VITE_SUPABASE_URL 
    : process.env.VITE_SUPABASE_URL;
    
  if (supabaseUrl) {
    try {
      const origin = new URL(supabaseUrl).origin;
      const supabaseWildcard = `https://*.supabase.co`;
      const supabaseWs = `wss://*.supabase.co`;
      
      // Connect for API calls and realtime
      directives['connect-src'].push(origin, supabaseWildcard, supabaseWs);
      
      // Images from Supabase storage
      directives['img-src'].push(origin, supabaseWildcard);
    } catch {
      console.warn('Invalid Supabase URL in CSP generation');
    }
  }
  
  // Add Stripe domains if enabled (comprehensive subdomain coverage)
  const stripeKey = typeof window !== 'undefined'
    ? import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY
    : process.env.VITE_STRIPE_PUBLISHABLE_KEY;
    
  if (stripeKey) {
    // Stripe JavaScript SDK and network
    directives['script-src'].push(
      'https://js.stripe.com',
      'https://m.stripe.network' // Stripe uses this domain for mobile
    );
    
    // Stripe iframes for 3D Secure, payment methods, etc.
    directives['frame-src'].push(
      'https://js.stripe.com',
      'https://checkout.stripe.com'
    );
    
    // Stripe API endpoints (comprehensive coverage)
    directives['connect-src'].push(
      'https://api.stripe.com',
      'https://hooks.stripe.com',
      'https://checkout.stripe.com',
      'https://m.stripe.com', // Mobile endpoints
      'https://m.stripe.network', // Mobile network
      'https://merchant-ui-api.stripe.com' // Merchant dashboard
    );
    
    // Stripe images (logos, icons)
    directives['img-src'].push(
      'https://b.stripecdn.com',
      'https://assets.stripe.com'
    );
    
    // Form actions for Stripe checkout
    directives['form-action'].push('https://checkout.stripe.com');
  }
  
  // Add analytics domains if enabled
  if (config.enableAnalytics) {
    directives['script-src'].push('https://va.vercel-scripts.com');
    directives['connect-src'].push('https://va.vercel-scripts.com');
  }
  
  if (config.enableGoogleAnalytics) {
    directives['script-src'].push(...SECURITY_ALLOWLISTS.analytics);
    directives['connect-src'].push(...SECURITY_ALLOWLISTS.analytics);
    directives['img-src'].push('https://www.google-analytics.com');
  }
  
  // Add external fonts if enabled
  if (config.enableExternalFonts) {
    directives['style-src'].push(...SECURITY_ALLOWLISTS.fonts.map(d => `https://${d}`));
    directives['font-src'].push(...SECURITY_ALLOWLISTS.fonts.map(d => `https://${d}`));
  }
  
  // Add CDN images if enabled
  if (config.enableCdnImages) {
    directives['img-src'].push(...SECURITY_ALLOWLISTS.images.map(d => `https://${d}`));
  }
  
  // Development mode additions
  if (config.isDevelopment) {
    directives['script-src'].push("'unsafe-eval'"); // For Vite HMR
    directives['connect-src'].push('ws://localhost:*', 'http://localhost:*');
  }
  
  // Add CSP reporting (only in production)
  const cspParts = Object.entries(directives)
    .filter(([, sources]) => Array.isArray(sources) ? sources.length > 0 : true)
    .map(([directive, sources]) => 
      Array.isArray(sources) && sources.length > 0
        ? `${directive} ${sources.join(' ')}`
        : directive
    );
  
  // Add reporting directives for production
  if (config.isProduction) {
    cspParts.push(`report-uri ${CSP_REPORTING.reportUri}`);
    cspParts.push(`report-to ${CSP_REPORTING.reportTo.group}`);
  }
  
  return cspParts.join('; ');
}

/**
 * Log security configuration for debugging
 */
export function logSecurityConfig(): void {
  const config = getSecurityConfig();
  
  if (config.enableDebugMode) {
    console.group('🔒 Security Configuration');
    console.log('Environment:', config.isDevelopment ? 'Development' : 'Production');
    console.log('Analytics:', config.enableAnalytics ? '✅' : '❌');
    console.log('Google Analytics:', config.enableGoogleAnalytics ? '✅' : '❌');
    console.log('External Fonts:', config.enableExternalFonts ? '✅' : '❌');
    console.log('CDN Images:', config.enableCdnImages ? '✅' : '❌');
    console.log('Error Reporting:', config.enableErrorReporting ? '✅' : '❌');
    console.log('CSP:', generateCSP(config));
    console.groupEnd();
  }
}
